import { useUiContext } from '@elementary/common-ui/components/AiChat';
import { UseFormReturn } from 'react-hook-form';
import { EnvironmentForm } from 'shared/types';
import { EnvironmentError } from '../../../../types';
import {
  CONTENT_STEP_INDEX,
  ENVIRONMENT_DRAWER_ACTIONS,
  MAX_PORT,
  MIN_PORT,
} from '../constants';
import {
  getAssetSourceRules,
  getSshPortWhenEnabling,
  getWarehouseTerms,
} from '../utils';
import { USER_ONLY_FIELD_REASON } from './constants';
import { EnvironmentFormFieldIssue } from './types';
import {
  describeAssetSources,
  findEnvironmentFieldSpec,
  flattenFormErrors,
  getEnvironmentFormHint,
  getEnvironmentFormNotes,
  getEnvironmentFormSteps,
  getEnvironmentStepFields,
  getMissingRequiredFields,
  getUserOnlyFieldStatus,
  isUserOnlyField,
  isWarehouseType,
  normalizeAssetSourcesWrite,
  normalizeEnvironmentFieldWrite,
  parseEnvironmentFormWrite,
  previewEnvironmentFormUpdate,
  resolveEnvironmentFormValues,
  setEnvironmentFormField,
} from './utils';

type Props = {
  methods: UseFormReturn<EnvironmentForm>;
  isOpen: boolean;
  currentStep: number;
  environmentId?: string;
  existingAssetSourceIds: string[];
  isSaving: boolean;
  isLoadingEnvironment: boolean;
  error?: EnvironmentError;
};

const useEnvironmentFormUiContext = ({
  methods,
  isOpen,
  currentStep,
  environmentId,
  existingAssetSourceIds,
  isSaving,
  isLoadingEnvironment,
  error,
}: Props) => {
  const isEditMode = !!environmentId;
  const steps = getEnvironmentFormSteps(isEditMode);
  const [connectionStep, contentStep] = steps;
  const isContentStep = currentStep === CONTENT_STEP_INDEX;
  const step = isContentStep ? contentStep : connectionStep;
  const otherStep = isContentStep ? connectionStep : contentStep;

  const getState = () => {
    const values = methods.getValues();
    const resolved = resolveEnvironmentFormValues(values);
    const fields = getEnvironmentStepFields(step.index, values);
    const editableFields = fields.filter(
      (spec) => !isUserOnlyField(spec) && spec.kind !== 'assetSources',
    );
    const warehouseType = isWarehouseType(values.warehouseType)
      ? values.warehouseType
      : undefined;
    const assetSourcesState =
      step.index === CONTENT_STEP_INDEX && warehouseType
        ? {
            assetSources: describeAssetSources(
              values.assetSources ?? [],
              existingAssetSourceIds,
            ),
            assetSourceRules: {
              ...getAssetSourceRules(warehouseType),
              ...getWarehouseTerms(warehouseType),
            },
          }
        : {};

    return {
      step,
      form: Object.fromEntries(
        editableFields.map((spec) => [spec.name, resolved[spec.name] ?? null]),
      ),
      ...assetSourcesState,
      writableFields: fields
        .filter((spec) => !isUserOnlyField(spec))
        .map((spec) => spec.name),
      userOnlyFields: fields.filter(isUserOnlyField).map((spec) => ({
        field: spec.name,
        label: spec.label,
        required: !!spec.required,
        status: getUserOnlyFieldStatus(spec, resolved[spec.name]),
      })),
      fieldOptions: Object.fromEntries(
        editableFields.flatMap((spec) =>
          spec.options
            ? [
                [
                  spec.name,
                  spec.allowsNone
                    ? [...spec.options, { value: null, label: 'None' }]
                    : spec.options,
                ],
              ]
            : [],
        ),
      ),
      missingRequiredFields: getMissingRequiredFields(
        step.index,
        values,
        existingAssetSourceIds,
      ),
      dirty: methods.formState.isDirty,
      hint: getEnvironmentFormHint({
        isSaving,
        isEditMode,
        stepIndex: step.index,
      }),
      notes: getEnvironmentFormNotes(step.index, resolved),
    };
  };

  const rejectAll = (reason: string) => ({
    applied: [],
    rejected: [{ field: 'form', reason }],
    ...getState(),
  });

  useUiContext(
    isOpen
      ? {
          read_environment_form: {
            description: `Read the open create/edit environment drawer on the Environments page, where users connect a data warehouse to Elementary (the onboarding flow). Returns mode (create or edit), the current step and what it means, all steps, saving, the live unsaved values of the non-secret fields visible on this step (form), writableFields, fieldOptions (the options of select fields, each with the value to write, the label the form shows, and an optional note such as Recommended or Deprecated; a null value means none), userOnlyFields (secrets and credential files with status empty, entered, or saved; their values are never returned), missingRequiredFields, validationErrors, submitError (the error from the last save attempt, such as a failed warehouse connection or a dbt project whose Elementary schema was not found), notes, and hint (what the user does next). On the ${contentStep.name} step it also returns assetSources (each with id, isExisting, and lockedFields) and assetSourceRules for the selected warehouse. Not available unless the drawer is open.`,
            handler: () => {
              if (isLoadingEnvironment) {
                return {
                  loading: true,
                  hint: 'The environment is still loading. Read again in a moment.',
                };
              }
              return {
                mode: isEditMode ? 'edit' : 'create',
                environmentId: environmentId ?? null,
                steps,
                saving: isSaving,
                validationErrors: flattenFormErrors(methods.formState.errors),
                submitError: error ?? null,
                ...getState(),
              };
            },
          },
          write_environment_form: {
            description: `Propose values for fields on the currently visible step of the open environment drawer. form is an object of field values using the field names from read_environment_form. Only writableFields can be set, plus fields that become visible in the same write (for example warehouseType: "snowflake" together with snowflakeAccount, or snowflakeAuthMethod together with snowflakeUser). Select fields only accept an option value from fieldOptions; use null for none where allowed. Refer to options by their label when talking to the user, and suggest the Recommended option when the user has no preference. Ports are integers between ${MIN_PORT} and ${MAX_PORT}. Secrets and credential files (userOnlyFields: passwords, private keys, tokens, secret keys, external IDs, key files) cannot be written: tell the user which ones to fill in directly in the form, and never ask them to paste secrets into the chat. On the ${contentStep.name} step, assetSources is the full list of asset sources and replaces the current list. Each item is { type: "dbt", name, database, schema }, where schema is the Elementary schema the dbt package writes to, or { type: "warehouse", name, dbFilters: [{ database, schemas: [] }] }, where empty schemas means all schemas in that database. Keep the id of saved sources to keep them; leaving a saved source out removes it when the user saves. A saved source cannot change type, and a saved dbt source cannot change database or schema. Follow assetSourceRules, since some warehouses have no database or no schemas. Does not move between steps or save: the user clicks ${ENVIRONMENT_DRAWER_ACTIONS.continue}, ${ENVIRONMENT_DRAWER_ACTIONS.saveAndSync}, or ${ENVIRONMENT_DRAWER_ACTIONS.saveChanges}. Requires user approval.`,
            requiresApproval: true,
            enabledModes: ['agent'],
            preview: previewEnvironmentFormUpdate,
            argsSchema: {
              type: 'object',
              properties: {
                form: { type: ['object', 'string'] },
                reason: { type: 'string' },
              },
            },
            handler: (args) => {
              if (isLoadingEnvironment) {
                return rejectAll('The environment is still loading');
              }
              if (isSaving) {
                return rejectAll(
                  'The environment is being saved; try again after saving finishes',
                );
              }
              const proposed = parseEnvironmentFormWrite(args);
              if (!proposed) {
                return rejectAll('form must be a JSON object of field values');
              }

              const rejected: EnvironmentFormFieldIssue[] = [];
              const normalized: Record<string, unknown> = {};
              Object.entries(proposed).forEach(([field, value]) => {
                const spec = findEnvironmentFieldSpec(field);
                if (!spec) {
                  rejected.push({ field, reason: 'Unknown field' });
                  return;
                }
                if (isUserOnlyField(spec)) {
                  rejected.push({ field, reason: USER_ONLY_FIELD_REASON });
                  return;
                }
                if (spec.kind === 'assetSources') {
                  normalized[field] = value;
                  return;
                }
                const result = normalizeEnvironmentFieldWrite(spec, value);
                if (result.ok) {
                  normalized[field] = result.value;
                } else {
                  rejected.push({ field, reason: result.reason });
                }
              });

              const current = methods.getValues();
              const nextValues = { ...current, ...normalized };
              const fields = getEnvironmentStepFields(step.index, nextValues);
              const otherStepFieldNames = getEnvironmentStepFields(
                otherStep.index,
                nextValues,
              ).map((spec) => spec.name);

              Object.keys(normalized)
                .filter((field) => !fields.some((spec) => spec.name === field))
                .forEach((field) => {
                  rejected.push({
                    field,
                    reason: otherStepFieldNames.some((name) => name === field)
                      ? `Belongs to the "${otherStep.name}" step; the user has to move to that step first`
                      : 'Not shown for the current warehouse or authentication method selection',
                  });
                });

              const applied: string[] = [];
              fields.forEach((spec) => {
                if (!(spec.name in normalized)) {
                  return;
                }
                if (spec.kind === 'assetSources') {
                  if (!isWarehouseType(current.warehouseType)) {
                    rejected.push({
                      field: spec.name,
                      reason: 'Select a data warehouse first',
                    });
                    return;
                  }
                  const result = normalizeAssetSourcesWrite(
                    normalized[spec.name],
                    {
                      currentSources: current.assetSources ?? [],
                      existingAssetSourceIds,
                      warehouseType: current.warehouseType,
                    },
                  );
                  if (!result.ok) {
                    rejected.push({ field: spec.name, reason: result.reason });
                    return;
                  }
                  setEnvironmentFormField(methods, spec.name, result.value, {
                    shouldDirty: true,
                  });
                  applied.push(spec.name);
                  return;
                }
                setEnvironmentFormField(
                  methods,
                  spec.name,
                  normalized[spec.name],
                  { shouldDirty: !spec.skipDirty },
                );
                applied.push(spec.name);
              });

              const { sshPort } = resolveEnvironmentFormValues(
                methods.getValues(),
              );
              const sshPortToSet =
                applied.includes('sshEnabled') && !('sshPort' in normalized)
                  ? getSshPortWhenEnabling(
                      normalized.sshEnabled === true,
                      sshPort,
                    )
                  : undefined;
              if (sshPortToSet !== undefined) {
                setEnvironmentFormField(methods, 'sshPort', sshPortToSet, {
                  shouldDirty: false,
                });
                applied.push('sshPort');
              }

              return { applied, rejected, ...getState() };
            },
          },
        }
      : {},
  );
};

export default useEnvironmentFormUiContext;
